import { Close, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material'
import type { BoxProps } from '@mui/material/Box';
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import { useTranslations } from 'next-intl'

import { useSettings } from '@core/context/settingsContext'

import HeaderBranding from '~/layouts/components/HeaderBranding'

interface Props {
  hidden: boolean
  toggleNavVisibility: () => void
}

/**
 * Single header wrapper whose layout morphs between expanded (row) and
 * collapsed (column) via CSS so the `HeaderBranding` node stays mounted.
 * That lets the bolt logo inside `HeaderBranding` smoothly transition its
 * size/position between the two states (small over the "y" in "Synop" →
 * large and centered) instead of snapping on remount.
 */
const MenuHeaderWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== 'collapsed' })<
  BoxProps & { collapsed: boolean }
>(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  // Expanded: bolt at the leading edge, chevron pinned trailing.
  // Collapsed: only the bolt sits in flex flow (chevron is anchored
  // absolutely over the drawer's right border – see the trailing controls
  // box below). `paddingLeft` is held constant across both states so the
  // bolt's global position barely shifts during the transition – it just
  // gets joined by the "Synop" wordmark fading in alongside it.
  justifyContent: 'space-between',
  position: 'relative',
  paddingLeft: theme.spacing(4),
  paddingRight: collapsed ? 0 : theme.spacing(4),
  minHeight: theme.mixins.toolbar.minHeight,
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['padding'], { duration: '.25s' })
}))

const VerticalNavHeader = (props: Props) => {
  const { hidden, toggleNavVisibility } = props
  const t = useTranslations('layouts.navDrawer')

  const { settings, saveSettings } = useSettings()
  const { navCollapsed } = settings

  const isCollapsed = !hidden && navCollapsed
  const toggleCollapse = () => saveSettings({ ...settings, navCollapsed: !navCollapsed })

  return (
    <MenuHeaderWrapper className="nav-header" data-cy="nav-header" collapsed={isCollapsed}>
      {/* `HeaderBranding` is rendered unconditionally so React keeps the same
          DOM/instance across collapse toggles. That preserves the bolt's
          internal width/opacity transitions instead of remounting them. */}
      <HeaderBranding />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          // When collapsed, lift the chevron out of flex flow and center it on
          // the drawer's right border. The translate keeps the button fully
          // visible (drawer paper has overflow:visible) while freeing the bolt
          // to center across the full rail width.
          ...(isCollapsed && {
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translate(50%, -50%)',
            zIndex: 1
          })
        }}
      >
        {hidden ? (
        <IconButton
          aria-label={t('closeNavMenu')}
          disableRipple
          onClick={toggleNavVisibility}
          sx={{ p: 0, backgroundColor: 'transparent !important' }}
        >
          <Close fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          aria-label={isCollapsed ? t('expandNav') : t('collapseNav')}
          data-cy={isCollapsed ? 'nav-expand-toggle' : 'nav-collapse-toggle'}
          disableRipple
          onClick={toggleCollapse}
          sx={{
            p: isCollapsed ? 0.5 : 1,
            flexShrink: 0,
            // Collapsed chevron straddles the drawer's right border, so it
            // gets an opaque fill matching the drawer to mask the divider
            // beneath it. No outline – it should read as a flat icon button.
            backgroundColor: (theme) =>
              isCollapsed
                ? theme.palette.mode === 'dark'
                  ? theme.palette.background.navCard
                  : theme.palette.background.layout
                : 'transparent',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          {isCollapsed ? <KeyboardDoubleArrowRight fontSize="small" /> : <KeyboardDoubleArrowLeft fontSize="small" />}
        </IconButton>
      )}
      </Box>
    </MenuHeaderWrapper>
  )
}

export default VerticalNavHeader
